/**
 * HowItWorks.jsx
 * Standalone component — drop anywhere on the site.
 * Requires the DM Mono + Playfair Display + DM Sans fonts to already be loaded,
 * or add the @import yourself if using it on a page that doesn't have them.
 */

const STEPS = [
  {
    number: "01",
    label: "Universe Scan",
    title: "Every U.S. stock. Every market day.",
    body: "We pull data on the entire U.S. equity universe (over 3,000 stocks) each morning before the open. No pre-filtering by index or popularity.",
    tag: "~3,000 stocks",
  },
  {
    number: "02",
    label: "Valuation Screen",
    title: "Smart DCF & EPV models.",
    body: "Each stock runs through a discounted cash-flow model with risk-adjusted discount rates and an earnings-power-value cross-check. Companies with red-flag financials, excessive debt, negative FCF, accounting anomalies are eliminated here.",
    tag: "~200 survive",
  },
  {
    number: "03",
    label: "Business Quality Rating",
    title: "An LLM reads the business.",
    body: "Survivors go through our business-quality model: moat strength, management quality, revenue predictability, competitive position, disruption risk, and more — scored and weighted into a single quality rating.",
    tag: "AI-powered",
  },
  {
    number: "04",
    label: "Daily List",
    title: "The ranked shortlist, ready by open.",
    body: "The top valued stocks by combined valuation and quality score are saved and in the stock list. The full process reruns every day the market is open.",
    tag: "Saved For You",
  },
];

const HowItWorks = () => {
  return (
    <section className="hiw-root">
      <style>{`
        .hiw-root {
          width: 100%;
          max-width: 860px;
          margin: 0 auto;
          padding: 0 24px 72px;
          font-family: var(--font-sans);
        }

        /* ── Section header ── */
        .hiw-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 12px;
          margin-bottom: 48px;
        }

        .hiw-eyebrow {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .hiw-eyebrow::before,
        .hiw-eyebrow::after {
          content: '';
          display: block;
          width: 24px;
          height: 1px;
          background: var(--accent);
          opacity: 0.4;
        }

        .hiw-title {
          font-family: var(--font-display);
          font-size: clamp(26px, 4vw, 38px);
          font-weight: 400;
          line-height: 1.1;
          letter-spacing: -0.02em;
          color: var(--text-primary);
        }

        .hiw-title em {
          font-style: italic;
          color: var(--accent);
        }

        .hiw-sub {
          font-size: 15px;
          color: var(--text-tertiary);
          line-height: 1.7;
          font-weight: 400;
          max-width: 440px;
        }

        /* ── Steps ── */
        .hiw-steps {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1px;
          background: var(--border-subtle);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .hiw-step {
          background: var(--surface-1);
          padding: 28px 28px 24px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
          transition: background var(--dur-fast) var(--ease-out);
        }

        .hiw-step:hover { background: var(--surface-2); }

        /* Arrow connector — right edge of cols 1 & 3 */
        .hiw-step:nth-child(odd)::after {
          content: '›';
          position: absolute;
          right: -13px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: var(--accent);
          opacity: 0.35;
          z-index: 2;
          line-height: 1;
        }

        .hiw-step-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
        }

        .hiw-step-num {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.08em;
        }

        .hiw-step-tag {
          font-family: var(--font-mono);
          font-size: 12px;
          padding: 3px 9px;
          border-radius: var(--radius-pill);
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          color: var(--accent);
          letter-spacing: 0.04em;
          white-space: nowrap;
        }

        .hiw-step-label {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--accent);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .hiw-step-title {
          font-family: var(--font-display);
          font-size: 18px;
          font-weight: 400;
          color: var(--text-primary);
          line-height: 1.2;
          letter-spacing: -0.01em;
        }

        .hiw-step-body {
          font-size: 14px;
          color: var(--text-tertiary);
          line-height: 1.65;
          font-weight: 400;
        }

        /* ── Bottom cadence bar ── */
        .hiw-cadence {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 20px;
          padding: 14px 20px;
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-md);
          background: rgba(52,211,153,0.02);
        }

        .hiw-cadence-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--accent);
          flex-shrink: 0;
          animation: hiw-pulse 2s infinite;
        }

        @keyframes hiw-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }

        .hiw-cadence-text {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.05em;
        }

        .hiw-cadence-text strong {
          color: var(--text-secondary);
          font-weight: 500;
        }

        @media (max-width: 640px) {
          .hiw-steps { grid-template-columns: 1fr; }
          .hiw-step:nth-child(odd)::after { display: none; }
          .hiw-step { padding: 22px 20px 20px; }
          .hiw-root { padding: 0 16px 56px; }
        }
      `}</style>

      <div className="hiw-header">
        <span className="hiw-eyebrow">How it works</span>
        <h2 className="hiw-title">
          Built to find what the
          <br />
          <em>market overlooks.</em>
        </h2>
        <p className="hiw-sub">
          A four-step pipeline runs every trading day — no manual curation, no
          gut calls.
        </p>
      </div>

      <div className="hiw-steps">
        {STEPS.map((step) => (
          <div className="hiw-step" key={step.number}>
            <div className="hiw-step-top">
              <span className="hiw-step-num">{step.number}</span>
              <span className="hiw-step-tag">{step.tag}</span>
            </div>
            <div className="hiw-step-label">{step.label}</div>
            <div className="hiw-step-title">{step.title}</div>
            <div className="hiw-step-body">{step.body}</div>
          </div>
        ))}
      </div>

      <div className="hiw-cadence">
        <span className="hiw-cadence-dot" />
        <span className="hiw-cadence-text">
          Runs automatically <strong>every day the U.S. market is open.</strong>{" "}
          Results are waiting before the bell.
        </span>
      </div>
    </section>
  );
};

export default HowItWorks;
